import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Flex,
  Box,
  Heading,
  Text,
  Button,
  Spinner,
  useColorModeValue,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthProvider';
import Card from '../../components/card/Card';

/**
 * Sign-in entry point. The actual credential collection (email/password, MFA,
 * social IdPs) is hosted by Zitadel — clicking Sign in redirects to the
 * configured OIDC authorize endpoint with PKCE. After authentication Zitadel
 * redirects back to /callback, which finalizes the session and returns here
 * via the location-state "from" path (or /dashboard).
 */
export function LoginPage() {
  const { t } = useTranslation();
  const { user, admin, signIn, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard';

  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const textColor = useColorModeValue('navy.700', 'white');
  const textColorSecondary = useColorModeValue('secondaryGray.600', 'secondaryGray.500');
  const bgColor = useColorModeValue('secondaryGray.300', 'navy.900');

  // If already signed in AND admin profile synced, redirect.
  React.useEffect(() => {
    if (user && !user.expired && admin && !authLoading) {
      navigate(from, { replace: true });
    }
  }, [user, admin, authLoading, navigate, from]);

  const handleSignIn = async () => {
    setError(null);
    setRedirecting(true);
    try {
      await signIn(from);
      // signIn() initiates a full-page redirect to Zitadel; this line rarely runs.
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t('admin.auth.errors.loginFailed');
      setError(message);
      setRedirecting(false);
    }
  };

  if (authLoading) {
    return (
      <Flex justify="center" align="center" minH="100vh" bg={bgColor}>
        <Spinner size="xl" color="brand.500" thickness="3px" />
      </Flex>
    );
  }

  return (
    <Flex
      justify="center"
      align="center"
      minH="100vh"
      bg={bgColor}
      data-testid="login-page"
    >
      <Card w={{ base: '95%', md: '420px' }} p="30px">
        <Heading
          as="h1"
          fontSize="36px"
          mb="10px"
          color={textColor}
          textAlign="center"
          data-testid="login-title"
        >
          {t('admin.auth.login.title')}
        </Heading>
        <Text
          mb="36px"
          color={textColorSecondary}
          fontWeight="400"
          fontSize="md"
          textAlign="center"
        >
          {t('admin.auth.login.subtitle')}
        </Text>

        {error && (
          <Box bg="red.50" p={3} borderRadius="16px" w="full" mb="24px" data-testid="login-error">
            <Text color="red.500" fontSize="sm">{error}</Text>
          </Box>
        )}

        <Button
          onClick={handleSignIn}
          variant="brand"
          isDisabled={redirecting}
          data-testid="login-submit-btn"
          w="full"
          h="44px"
          borderRadius="16px"
          fontSize="sm"
          fontWeight="bold"
        >
          {redirecting ? <Spinner size="sm" /> : t('admin.auth.login.signIn')}
        </Button>
      </Card>
    </Flex>
  );
}
